/**
 * FlightOpenerExportPanel — operator download of the people who opened an email in this
 * flight (Name, Phone, Member Status, Member ID, Email Address, plus Open Type).
 *
 * Modelled on FlightUnsubscribeExportPanel, and gated identically — the file is served ONLY
 * by the JWT + management-role gated `export-flight-openers` edge function, and the browser
 * never fetches raw addresses to build it. This panel:
 *   - is visible only to flight-management roles (admin / superadmin / manager);
 *   - loads counts (no addresses) to drive the disabled zero-state + metric;
 *   - shows loading / success / actionable-failure states on download;
 *   - states the machine-open caveat OUT LOUD, because a raw opener list is mostly robots.
 */
import { useEffect, useState } from "react";
import { useAuth } from "../../contexts/AuthContext";
import {
  downloadFlightOpenersCsv,
  getFlightOpenerSummary,
  type FlightOpenerSummary,
} from "../../services/campaigns.service";

// Mirrors the server's FLIGHT_MGMT_ROLES (admin / superadmin / manager). UI-only; the edge
// function re-derives and re-checks the role server-side.
const EXPORT_ROLES = new Set(["admin", "superadmin", "manager"]);

interface Props {
  flightId: string;
}

export default function FlightOpenerExportPanel({ flightId }: Props) {
  const { user } = useAuth();
  const role = (user?.role ?? "").toString().toLowerCase();
  const canExport = EXPORT_ROLES.has(role);

  const [summary, setSummary] = useState<FlightOpenerSummary | null>(null);
  const [loadingSummary, setLoadingSummary] = useState(true);
  const [summaryError, setSummaryError] = useState<string | null>(null);

  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const [downloadSuccess, setDownloadSuccess] = useState<string | null>(null);

  useEffect(() => {
    if (!canExport) {
      return;
    }
    let cancelled = false;
    setLoadingSummary(true);
    setSummaryError(null);
    getFlightOpenerSummary(flightId)
      .then((s) => {
        if (!cancelled) {
          setSummary(s);
        }
      })
      .catch((e: unknown) => {
        if (!cancelled) {
          setSummaryError(
            e instanceof Error ? e.message : "Failed to load opener count",
          );
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoadingSummary(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [flightId, canExport]);

  if (!canExport) {
    return null;
  }

  const people = summary?.people ?? 0;
  const human = summary?.human ?? 0;
  const machine = summary?.machine ?? 0;
  const unclassified = summary?.unclassified ?? 0;
  const hasOpens = people > 0;

  const handleDownload = async () => {
    setDownloading(true);
    setDownloadError(null);
    setDownloadSuccess(null);
    try {
      const res = await downloadFlightOpenersCsv(flightId);
      setDownloadSuccess(
        `Downloaded ${res.rowCount.toLocaleString()} ${res.rowCount === 1 ? "person" : "people"} ` +
          `(${res.human.toLocaleString()} human, ${res.machine.toLocaleString()} machine, ` +
          `${res.unclassified.toLocaleString()} unclassified).`,
      );
    } catch (e: unknown) {
      setDownloadError(e instanceof Error ? e.message : "Download failed");
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="px-6 pb-6 pt-2 border-t border-gray-100">
      <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div className="min-w-0">
            <div className="text-sm font-semibold text-gray-900">
              Email opens
            </div>
            <p className="mt-1 text-sm text-gray-600">
              Download everyone who opened an email in this flight — name,
              phone, member status, member ID and email address. One row per
              person, so somebody who opened five times appears once.
            </p>
            <p className="mt-1 text-xs text-gray-500">
              Heads up: most &ldquo;opens&rdquo; are not people. Apple Mail
              Privacy Protection and corporate security scanners open mail
              automatically, and on measured traffic that is roughly 71% of all
              opens. The file therefore carries an <strong>Open Type</strong>{" "}
              column (Human / Machine / Unclassified) from SendGrid&rsquo;s own
              verdict — nothing is filtered out for you.{" "}
              <strong>Unclassified</strong> means no verdict was retained for
              that open, which is the case for flights sent before this was
              captured; it does not mean a human opened it.
            </p>
            {loadingSummary ? (
              <p className="mt-2 text-sm text-gray-500">Checking for opens…</p>
            ) : summaryError ? (
              <p className="mt-2 text-sm text-red-600">{summaryError}</p>
            ) : (
              <p className="mt-2 text-sm text-gray-700">
                {hasOpens ? (
                  <>
                    {people.toLocaleString()}{" "}
                    {people === 1 ? "person" : "people"} opened —{" "}
                    {human.toLocaleString()} human, {machine.toLocaleString()}{" "}
                    machine, {unclassified.toLocaleString()} unclassified.
                  </>
                ) : (
                  "No opens recorded for this flight yet."
                )}
              </p>
            )}
          </div>
          <div className="shrink-0">
            <button
              type="button"
              onClick={handleDownload}
              disabled={!hasOpens || downloading || loadingSummary}
              title={
                !hasOpens
                  ? "No opens recorded for this flight yet"
                  : "Download the openers CSV"
              }
              className="inline-flex items-center gap-2 rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-gray-300 disabled:text-gray-500"
            >
              {downloading ? "Preparing…" : "Download openers CSV"}
            </button>
          </div>
        </div>
        {downloadError && (
          <div className="mt-3 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
            {downloadError}{" "}
            <button
              type="button"
              onClick={handleDownload}
              className="font-medium underline hover:no-underline"
            >
              Try again
            </button>
          </div>
        )}
        {downloadSuccess && (
          <div className="mt-3 rounded-md border border-green-200 bg-green-50 px-3 py-2 text-sm text-green-700">
            {downloadSuccess}
          </div>
        )}
      </div>
    </div>
  );
}
